import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LOYALTY_GOAL, LOYALTY_REWARD, LOYALTY_TIERS,
  loyaltyHint, markLoyaltySeen, nextTier, plural,
  type LoyaltyStatus,
} from '../lib/loyalty';
import './Loyalty.css';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const isTier = (n: number) => (LOYALTY_TIERS as readonly number[]).includes(n);

function addDays(iso: string, n: number) {
  const d = new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10) + n));
  return d.toISOString().slice(0, 10);
}

/* ─── Flame badge: consecutive weeks with ≥ 3 visits ─── */
export function StreakFlame({ weeks, size = 'md' }: { weeks: number; size?: 'md' | 'lg' }) {
  return (
    <div className={`lw-flame lw-flame--${size} ${weeks > 0 ? 'lw-flame--on' : ''}`}>
      <span className="lw-flame__icon">🔥</span>
      <span className="lw-flame__num">{weeks}</span>
      <span className="lw-flame__label">{plural(weeks, 'неделя', 'недели', 'недель')}<br />подряд</span>
    </div>
  );
}

/* ─── Five poker chips, one per visit; rewards hang over chips 3 and 5 ─── */
export function LoyaltyTrack({ visits, rewards }: { visits: number; rewards: number[] }) {
  const filled = Math.min(visits, LOYALTY_GOAL);
  const next = filled < LOYALTY_GOAL ? filled + 1 : null;
  return (
    <div className="lw-track" style={{ ['--lw-progress' as any]: `${(Math.max(filled - 1, 0) / (LOYALTY_GOAL - 1)) * 100}%` }}>
      <div className="lw-track__rail"><div className="lw-track__fill" /></div>
      {Array.from({ length: LOYALTY_GOAL }, (_, i) => {
        const n = i + 1;
        const on = n <= filled;
        const earned = rewards.includes(n);
        return (
          <div key={n} className="lw-slot" style={{ ['--i' as any]: i }}>
            {isTier(n) && (
              <div className={`lw-prize ${earned ? 'lw-prize--earned' : ''}`}>
                <span className="lw-prize__coin" />
                {earned ? '✓ ' : '+'}{LOYALTY_REWARD.toLocaleString('ru-RU')}
              </div>
            )}
            <div className={`lw-chip ${on ? 'lw-chip--on' : ''} ${n === next ? 'lw-chip--next' : ''} ${isTier(n) ? 'lw-chip--tier' : ''}`}>
              <span>{on ? '♠' : n}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Mon..Sun strip with the visited days ─── */
export function LoyaltyWeekdays({ status }: { status: LoyaltyStatus }) {
  const visited = new Set(status.visit_days);
  return (
    <div className="lw-days">
      {WEEKDAYS.map((label, i) => {
        const day = addDays(status.week_start, i);
        const cls = visited.has(day) ? 'lw-day--visit' : day === status.today ? 'lw-day--today' : day < status.today ? 'lw-day--past' : '';
        return (
          <div key={label} className={`lw-day ${cls}`}>
            <span className="lw-day__dot" />
            <span className="lw-day__label">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Home card ─── */
export function LoyaltyHomeCard({ status, onOpen }: { status: LoyaltyStatus; onOpen: () => void }) {
  const done = nextTier(status.visits) === null;
  return (
    <button type="button" className={`lw-card ${done ? 'lw-card--done' : ''}`} onClick={onOpen}>
      <div className="lw-card__head">
        <div>
          <div className="lw-eyebrow">Недельная серия</div>
          <div className="lw-count">
            <b>{Math.min(status.visits, LOYALTY_GOAL)}</b><span>/{LOYALTY_GOAL}</span>
            <em>{plural(status.visits, 'визит', 'визита', 'визитов')}</em>
          </div>
        </div>
        <StreakFlame weeks={status.streak} />
      </div>
      <LoyaltyTrack visits={status.visits} rewards={status.week_rewards} />
      <LoyaltyWeekdays status={status} />
      <div className="lw-hint">
        <span>{loyaltyHint(status)}</span>
        <span className="lw-hint__arrow">›</span>
      </div>
    </button>
  );
}

/* ─── Full-screen celebration for freshly earned bonuses ─── */
export function LoyaltyCelebration({ userId, status, onClose }: { userId: number; status: LoyaltyStatus; onClose: () => void }) {
  const total = status.unseen.reduce((s, r) => s + r.amount, 0);
  const topTier = Math.max(...status.unseen.map(r => r.tier));
  const [count, setCount] = useState(0);

  // Count-up of the amount for a bit of drama.
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / 1100);
      setCount(Math.round(total * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  const coins = useMemo(() => Array.from({ length: 22 }, () => ({
    left: Math.random() * 100, delay: Math.random() * 2.2, dur: 2.4 + Math.random() * 2, size: 18 + Math.random() * 22,
  })), []);

  const claim = () => { markLoyaltySeen(userId).catch(() => {}); onClose(); };

  return createPortal(
    <div className="lw-cel" onClick={claim}>
      <div className="lw-cel__rays" />
      {coins.map((c, i) => (
        <span key={i} className="lw-cel__drop" style={{ left: `${c.left}%`, fontSize: c.size, animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s` }}>🪙</span>
      ))}
      <div className="lw-cel__box" onClick={e => e.stopPropagation()}>
        <div className="lw-cel__eyebrow">Бонус за серию</div>
        <div className="lw-cel__coin"><span>H</span></div>
        <div className="lw-cel__amount">+{count.toLocaleString('ru-RU')}</div>
        <div className="lw-cel__unit">HUcoin</div>
        <p className="lw-cel__text">
          {topTier >= LOYALTY_GOAL
            ? <>5 визитов за неделю — <b>максимум собран!</b></>
            : <>3 визита за неделю. Дойди до <b>5</b> — и получишь ещё <b>+{LOYALTY_REWARD.toLocaleString('ru-RU')}</b></>}
        </p>
        <button type="button" className="lw-cel__btn" onClick={claim}>Забрать</button>
      </div>
    </div>,
    document.body
  );
}

/* ─── "What is HUcoin" sheet ─── */
export function HucoinInfo({ onClose }: { onClose: () => void }) {
  return createPortal(
    <div className="lw-sheet" onClick={onClose}>
      <div className="lw-sheet__panel" onClick={e => e.stopPropagation()}>
        <button type="button" className="lw-sheet__close" onClick={onClose} aria-label="Закрыть">✕</button>
        <div className="lw-sheet__eyebrow">💰 Встречайте</div>
        <h2 className="lw-sheet__title">HUcoin</h2>
        <p>У нас появилась внутренняя валюта клуба — HUcoin 🔄</p>
        <p>Теперь часть ваших наград и бонусов можно конвертировать во внутреннюю валюту и потратить прямо в HEADS UP!</p>

        <h3>Где посмотреть баланс?</h3>
        <p>Баланс HUcoin отображается прямо в приложении в вашем профиле. Тапаете на него и можете посмотреть историю всех операций. Также баланс всегда можно уточнить у администратора.</p>

        <h3>🤑 Как заработать HUcoin?</h3>
        <ul>
          <li>участвовать в наших розыгрышах HUcoin</li>
          <li>получать HUcoin за специальные активности и мероприятия клуба</li>
          <li>обменивать поездку на такси до дома на HUcoin</li>
          <li>работать дилером: 2 часа = 1 000 HUcoin на ваш баланс</li>
        </ul>

        <h3>💳 На что потратить HUcoin?</h3>
        <div className="lw-spend">
          <div className="lw-spend__rate">Игра · 1 HU = 1 ₽</div>
          <div className="lw-spend__items">стартовый стек · ре-энтри · аддоны</div>
          <div className="lw-spend__cap">можно оплатить до <b>100%</b> заказа</div>
        </div>
        <div className="lw-spend">
          <div className="lw-spend__rate">Бар · 1 HU = 1 ₽</div>
          <div className="lw-spend__items">еда · напитки</div>
          <div className="lw-spend__cap">можно оплатить до <b>50%</b> заказа</div>
        </div>

        <p className="lw-sheet__note">❗️ Оплата HUcoin производится только через администратора. В приложении пока принимается только оплата в рублях, HUcoin там не списываются.</p>
        <p className="lw-sheet__end">Так что проверяйте баланс. Возможно, вы уже богаче, чем вам кажется!</p>
      </div>
    </div>,
    document.body
  );
}
